import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../../../components/ui';
import { usePhone } from '../PhoneContext';

/**
 * PopoutPhone — a small always-on-top window that mirrors the active call
 * and can hang up, mute or hold it.
 *
 * Why: the softphone lives in the tab that placed the call, so a rep who
 * opens the CRM in a second tab (or another app entirely) has to hunt for
 * the original tab just to hang up. A popout window floats above
 * everything, so the controls are always one click away.
 *
 * The WebRTC session deliberately stays in the parent tab — moving media
 * between windows would mean re-registering SIP and dropping the call.
 * The popout is a remote control: it posts commands back to the opener
 * and renders whatever state the opener broadcasts.
 */

/** Messages the popout and the opener exchange. */
type PopoutMsg =
  | { type: 'state'; call: MirroredCall | null }
  | { type: 'hangup' }
  | { type: 'mute'; on: boolean }
  | { type: 'hold'; on: boolean }
  | { type: 'ready' };

interface MirroredCall {
  name: string;
  number: string;
  state: string;
  muted: boolean;
  held: boolean;
  startedAt: number | null;
}

const CHANNEL = 'matat-phone-popout';
const POPUP_FEATURES = 'width=340,height=260,menubar=no,toolbar=no,location=no,status=no,resizable=yes';

/** Shared by both sides; BroadcastChannel works across same-origin windows. */
function useChannel(onMsg: (m: PopoutMsg) => void) {
  const ref = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(CHANNEL);
    ch.onmessage = (e) => onMsg(e.data as PopoutMsg);
    ref.current = ch;
    return () => { ch.close(); ref.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (m: PopoutMsg) => ref.current?.postMessage(m);
}

/**
 * Mounted in the main app. Broadcasts call state for any popout to render,
 * and applies commands the popout sends back.
 *
 * Renders the button that opens the popout — only while a call is up,
 * since there is nothing to control otherwise.
 */
export const PopoutPhoneHost: React.FC = () => {
  const { call, hangup, toggleMute, toggleHold } = usePhone();
  const [popout, setPopout] = useState<Window | null>(null);

  const send = useChannel((m) => {
    // Commands from the popout window.
    if (m.type === 'hangup') hangup();
    else if (m.type === 'mute') toggleMute();
    else if (m.type === 'hold') toggleHold();
    else if (m.type === 'ready') broadcast();
  });

  const broadcast = React.useCallback(() => {
    send({
      type: 'state',
      call: call
        ? {
            name: call.party?.customer?.fullName || call.number,
            number: call.number,
            state: call.state,
            muted: !!call.muted,
            held: call.state === 'held',
            startedAt: call.answeredAt ?? null,
          }
        : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call]);

  // Mirror every state change to the popout.
  useEffect(() => { broadcast(); }, [broadcast]);

  // Close the popout when the call ends — a stale window showing a dead
  // call is worse than no window.
  useEffect(() => {
    if (!call && popout && !popout.closed) {
      popout.close();
      setPopout(null);
    }
  }, [call, popout]);

  // Don't leave an orphan window behind if this tab goes away.
  useEffect(() => {
    const onUnload = () => { if (popout && !popout.closed) popout.close(); };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [popout]);

  if (!call) return null;

  const open = () => {
    if (popout && !popout.closed) { popout.focus(); return; }
    const w = window.open('/phone-popout', 'matat-phone', POPUP_FEATURES);
    if (!w) return; // blocked by the popup blocker
    setPopout(w);
  };

  return (
    <button
      onClick={open}
      title="פתח חלון שיחה צף — לניתוק מכל מסך"
      aria-label="פתח חלון שיחה צף"
      className="size-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 flex items-center justify-center transition"
    >
      <Icon name="open_in_new" size={18} />
    </button>
  );
};

/** "3:42" since answer. */
function elapsed(from: number | null): string {
  if (!from) return '';
  const s = Math.max(0, Math.floor((Date.now() - from) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * The popout window's own page (route: /phone-popout). Renders the
 * mirrored call and sends commands back to the opener.
 */
export const PopoutPhoneWindow: React.FC = () => {
  const [call, setCall] = useState<MirroredCall | null>(null);
  const [, tick] = useState(0);

  const send = useChannel((m) => {
    if (m.type === 'state') setCall(m.call);
  });

  // Ask the opener for the current state as soon as we mount.
  useEffect(() => { send({ type: 'ready' }); }, [send]);

  // Re-render once a second so the timer counts up.
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // The opener closes us when the call ends; this covers the opener being
  // gone entirely (tab closed mid-call).
  useEffect(() => {
    const t = setInterval(() => {
      if (!window.opener || window.opener.closed) window.close();
    }, 2000);
    return () => clearInterval(t);
  }, []);

  return (
    <div dir="rtl" className="h-screen w-screen bg-slate-900 text-slate-100 flex flex-col items-center justify-center p-4 select-none">
      {!call ? (
        <div className="text-sm text-slate-400">אין שיחה פעילה</div>
      ) : (
        <>
          <div className="text-lg font-bold truncate max-w-full">{call.name}</div>
          <div className="text-xs text-slate-400 tabular-nums mt-0.5" dir="ltr">{call.number}</div>
          <div className="text-sm text-emerald-400 tabular-nums mt-2">
            {call.state === 'held' ? 'בהמתנה' : elapsed(call.startedAt) || 'מתחבר…'}
          </div>

          <div className="flex items-center gap-3 mt-5">
            <button
              onClick={() => send({ type: 'mute', on: !call.muted })}
              title={call.muted ? 'בטל השתקה' : 'השתק'}
              className={`size-11 rounded-full flex items-center justify-center transition ${
                call.muted ? 'bg-amber-500 text-white' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
              }`}
            >
              <Icon name={call.muted ? 'mic_off' : 'mic'} size={20} />
            </button>

            <button
              onClick={() => send({ type: 'hangup' })}
              title="נתק"
              className="size-14 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition"
            >
              <Icon name="call_end" size={26} />
            </button>

            <button
              onClick={() => send({ type: 'hold', on: !call.held })}
              title={call.held ? 'המשך שיחה' : 'המתנה'}
              className={`size-11 rounded-full flex items-center justify-center transition ${
                call.held ? 'bg-amber-500 text-white' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
              }`}
            >
              <Icon name={call.held ? 'play_arrow' : 'pause'} size={20} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
